import React from 'react';
import {
  ClockIcon,
  TruckIcon,
  MapPinIcon,
  CheckCircleIcon,
  PlayCircleIcon,
} from '@heroicons/react/24/outline';
import { PlayCircleIcon as PlayCircleIconSolid } from '@heroicons/react/24/solid';
import type { EnrichedCase } from '../../types';
import { TimeDisplay } from './TimeDisplay';

interface HighlightCaseCardProps {
  case: EnrichedCase & {
    vehicleName: string;
    pickupPartnerName: string;
    deliveryPartnerName: string;
    pickupPrefecture: string; // FL-188: 地域
    vehiclePlateNumber: string; // FL-188: 車体番号
    timeInfo: {
      startTime: string;
      endTime: string;
      label: string;
    };
  };
  type: 'current' | 'next';
  onStatusUpdate?: (caseId: string, newStatus: 'in_transit' | 'completed') => void;
}

/**
 * 重要案件ハイライトカード
 * 現在作業中の案件や次の案件を強調表示するカード
 */
export const HighlightCaseCard: React.FC<HighlightCaseCardProps> = ({
  case: caseData,
  type,
  onStatusUpdate,
}) => {
  const isCurrentCase = type === 'current';
  const bgColor = isCurrentCase
    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700'
    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700';

  const iconColor = isCurrentCase
    ? 'text-yellow-600 dark:text-yellow-400'
    : 'text-blue-600 dark:text-blue-400';

  const handleStatusUpdate = (newStatus: 'in_transit' | 'completed') => {
    onStatusUpdate?.(caseData.id, newStatus);
  };

  const isUnassigned = !caseData.assignedLandTransporterId;

  return (
    <div className={`rounded-lg border-2 p-4 ${bgColor}`}>
      <div className='flex items-center gap-3 mb-3'>
        {isCurrentCase ? (
          <PlayCircleIconSolid className={`h-6 w-6 ${iconColor}`} />
        ) : (
          <ClockIcon className={`h-6 w-6 ${iconColor}`} />
        )}
        <div>
          <div className='text-sm font-medium text-gray-700 dark:text-gray-300'>
            {isCurrentCase ? '作業中' : '次の案件'}
          </div>
          {/* 時間表示: 未割り当て時のみ表示 */}
          {isUnassigned && (
            <TimeDisplay
              pickupTimeStart={caseData.pickupTimeStart}
              pickupTimeEnd={caseData.pickupTimeEnd}
              deliveryTime={caseData.deliveryTime}
              textSize='text-sm'
              spacing='space-y-1'
            />
          )}
        </div>
      </div>

      <div className='space-y-2 mb-4'>
        <div className='flex items-start gap-2'>
          <TruckIcon className='h-4 w-4 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0' />
          <div className='text-sm text-gray-900 dark:text-white'>{caseData.vehicleName}</div>
        </div>
        {/* FL-188 + FL-191: 地域・ナンバープレート・車体番号 */}
        <div className='flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300'>
          <MapPinIcon className='h-4 w-4 text-gray-400 dark:text-gray-500' />
          <span>{caseData.pickupPrefecture}</span>
          <span className='text-gray-400 dark:text-gray-600'>|</span>
          <span className='font-mono'>{caseData.vehiclePlateNumber}</span>
          {caseData.chassisNumber && (
            <>
              <span className='text-gray-400 dark:text-gray-600'>|</span>
              <span className='font-mono text-xs'>車体: {caseData.chassisNumber}</span>
            </>
          )}
        </div>
        <div className='flex items-start gap-2'>
          <MapPinIcon className='h-4 w-4 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0' />
          <div className='text-sm text-gray-900 dark:text-white'>
            {caseData.pickupPartnerName} → {caseData.deliveryPartnerName}
          </div>
        </div>
      </div>

      {/* 陸送員操作ボタン（陸送員のみ表示） */}
      {onStatusUpdate && (
        <div className='flex gap-2'>
          {caseData.status === 'assigned' && (
            <button
              type='button'
              onClick={() => handleStatusUpdate('in_transit')}
              className='flex-1 inline-flex items-center justify-center px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors min-h-[44px]'
            >
              <PlayCircleIcon className='h-5 w-5 mr-2' />
              作業開始
            </button>
          )}
          {caseData.status === 'in_transit' && (
            <button
              type='button'
              onClick={() => handleStatusUpdate('completed')}
              className='flex-1 inline-flex items-center justify-center px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors min-h-[44px]'
            >
              <CheckCircleIcon className='h-5 w-5 mr-2' />
              完了報告
            </button>
          )}
        </div>
      )}
    </div>
  );
};
